import { Package } from 'pages/newSubscription/models';
import { ISchoolDetails } from 'pages/subscribe/models';
import { AccountContext } from 'subscriptionOptions/types';
import { PromoCodeSlots } from 'subscriptionOptions/promoCodeSlots';
import { Store } from './rootReducer';

/*STATE*/
export type State = {
  acceptCookies: boolean;
  accessibilityMode: boolean;
  mobileMenuOpen: boolean;
  toastStatus: { open: boolean; message: string };
  errorToastStatus: { open: boolean; message: string };
  selectedPackages: Package[];
  selectedPackageIds: number[];
  validPromoCode: string[];
  blogIds: number[];
  caseStudiesIds: number[];
  subscriptionPrice: {
    price: number;
    vat: number;
    total: number;
    discount: number;
    discountType: string;
  };
  updateUsersList: boolean;
  myDocumentsList: boolean;
  myDocumentsListSubjectId: number | null;
  myDocumentsListParentFolderId: number | null;
  myDocumentsListParentFolderName: string | null;
  schoolDetails: ISchoolDetails | null;
  registrationToken: string;
  membershipOptionInfo: any;
  quotesId: number | null;
  poNumber: string;
  FinanceEmail: string;
  secondUserDetails: { 
    firstName: string; 
    lastName: string; 
    emailAddress: string; 
    password: string; 
    confirmPassword: string; 
  } | null;
  secondExistingUserDetails: {
    mainAccountHolderSchoolUsersId: number;
  } | null;
  addOrUpgradeUserDetails: {
    chooseAddOrUpgrade: boolean;
    addSecondSchoolUser: boolean;
  } | null;
  hideCookieBanner: boolean;
  cancelSubConfirmMsg: boolean;
  /** Account context from the last options call: VAT, finance email, upgrade flags. */
  schoolAccountContext: AccountContext | null;
  /**
   * The promo code each subject holds, keyed by subjectsID. Held here because the promo box
   * unmounts at step 2 while the codes must survive to the payment call.
   */
  quotePromoCodeSlots: PromoCodeSlots;
};

export const initialState: State = {
  accessibilityMode: false,
  acceptCookies: false,
  mobileMenuOpen: false,
  toastStatus: { open: false, message: '' },
  errorToastStatus: { open: false, message: '' },
  selectedPackages: [],
  selectedPackageIds: [],
  validPromoCode: [],
  blogIds: [],
  caseStudiesIds: [],
  subscriptionPrice: {
    price: 0,
    vat: 0,
    total: 0,
    vatRate: 0,
    discount: 0,
    discountType: '',
  },
  poNumber: '',
  FinanceEmail: '',
  updateUsersList: true,
  myDocumentsList: true,
  myDocumentsListSubjectId: null,
  myDocumentsListParentFolderId: null,
  myDocumentsListParentFolderName: null,
  schoolDetails: null,
  registrationToken: '',
  secondUserDetails: null,
  secondExistingUserDetails: null,
  addOrUpgradeUserDetails: null,
  membershipOptionInfo: null,
  quotesId: null,
  hideCookieBanner: false,
  cancelSubConfirmMsg: false,
  schoolAccountContext: null,
  quotePromoCodeSlots: {},
};

/* ACTIONS */
export enum GeneralAction {
  TOGGLE_MOBILE_MENU = 'app/TOGGLE_MOBILE_MENU',
  ACCEPT_COOKIES = 'app/ACCEPT_COOKIES',
  TOGGLE_ACCESSIBILITY_MODE = 'app/TOGGLE_ACCESSIBILITY_MODE',
  TOGGLE_TOAST = 'app/TOGGLE_TOAST',
  TOGGLE_ERROR_TOAST = 'app/TOGGLE_ERROR_TOAST',
}

export enum PricingAction {
  SELECT_PACKAGES = 'app/SELECT_PACKAGES',
  SELECT_PACKAGE_IDS = 'app/SELECT_PACKAGE_IDS',
  SET_VALID_PROMO_CODE = 'app/SET_VALID_PROMO_CODE',
  CLEAR_SELECTED_PACKAGE = 'app/CLEAR_SELECTED_PACKAGE',
  SET_PO_NUMBER = 'app/SET_PO_NUMBER',
  CLEAR_PO_NUMBER = 'app/CLEAR_PO_NUMBER',
  SET_FINANCE_EMAIL = 'app/SET_FINANCE_EMAIL',
  CLEAR_FINANCE_EMAIL = 'app/CLEAR_FINANCE_EMAIL',  
  SET_SECOND_USER_DETAILS = 'app/SET_SECOND_USER_DETAILS',
  SET_EXISTING_SECOND_USER = 'app/SET_EXISTING_SECOND_USER',
  SET_ADD_OR_UPGRADE_USER_DETAILS = 'app/SET_ADD_OR_UPGRADE_USER_DETAILS',
  SET_SCHOOL_ACCOUNT_CONTEXT = 'app/SET_SCHOOL_ACCOUNT_CONTEXT',
  SET_QUOTE_PROMO_CODE_SLOTS = 'app/SET_QUOTE_PROMO_CODE_SLOTS',
}

export enum BlogAction {
  SET_BLOG_IDS = 'app/SET_BLOG_IDS',
}

export enum CaseStudyAction {
  SET_CASE_STUDIES_IDS = 'app/SET_CASE_STUDIES_IDS',
}

export enum SubscribeAction {
  SET_PRICE = 'app/SET_PRICE',
  SET_SCHOOL_DETAILS = 'app/SET_SCHOOL_DETAILS',
  CLEAR_SCHOOL_DETAILS = 'app/CLEAR_SCHOOL_DETAILS',
  SET_REGISTRATION_TOKEN = 'app/SET_REGISTRATION_TOKEN',
  SET_CANCEL_SUB_CONFIRM_MSG = 'app/SET_CANCEL_SUB_CONFIRM_MSG',
  SET_MEMBERSHIP_OPTION_INFO = 'app/SET_MEMBERSHIP_OPTION_INFO',
  QUOTES_ID = 'app/QUOTES_ID',
}

export enum ManageUsersAction {
  SET_UPDATE_USERS_LIST = 'app/SET_USERS_LIST',
}

export enum MyDocumentsAction {
  SET_UPDATE_DOCUMENTS_LIST = 'app/SET_UPDATE_DOCUMENTS_LIST',
  SET_UPDATE_DOCUMENTS_LIST_SUBJECT_ID = 'app/SET_UPDATE_DOCUMENTS_LIST_SUBJECT_ID',
  SET_UPDATE_DOCUMENTS_LIST_PARENT_FOLDER = 'app/SET_UPDATE_DOCUMENTS_LIST_PARENT_FOLDER',
}

export enum CookieBanner {
  SET_HIDE_COOKIE_BANNER = 'app/SET_HIDE_COOKIE_BANNER',
}

export type Action =
  | { type: GeneralAction.TOGGLE_MOBILE_MENU }
  | { type: GeneralAction.ACCEPT_COOKIES }
  | { type: GeneralAction.TOGGLE_ACCESSIBILITY_MODE }
  | {
      type: GeneralAction.TOGGLE_TOAST;
      payload: { open: boolean; message: string };
    }
  | {
      type: GeneralAction.TOGGLE_ERROR_TOAST;
      payload: { open: boolean; message: string };
    }
  | { type: PricingAction.SELECT_PACKAGES; payload: Package[] }
  | { type: PricingAction.SELECT_PACKAGE_IDS; payload: number[] }
  | { type: PricingAction.CLEAR_SELECTED_PACKAGE }
  | { type: PricingAction.SET_VALID_PROMO_CODE; payload: string[] }
  | { type: PricingAction.SET_PO_NUMBER; payload: string }
  | { type: PricingAction.CLEAR_PO_NUMBER }
  | { type: PricingAction.SET_FINANCE_EMAIL; payload: string }
  | { type: PricingAction.CLEAR_FINANCE_EMAIL }  
  | { type: PricingAction.SET_SECOND_USER_DETAILS; payload: any }
  | { type: PricingAction.SET_EXISTING_SECOND_USER; payload: any }
  | { type: PricingAction.SET_ADD_OR_UPGRADE_USER_DETAILS; payload: any }
  | { type: PricingAction.SET_SCHOOL_ACCOUNT_CONTEXT; payload: AccountContext | null }
  | { type: PricingAction.SET_QUOTE_PROMO_CODE_SLOTS; payload: PromoCodeSlots }
  | { type: BlogAction.SET_BLOG_IDS; payload: number[] }
  | { type: CaseStudyAction.SET_CASE_STUDIES_IDS; payload: number[] }
  | {
      type: SubscribeAction.SET_PRICE;
      payload: {
        price: number;
        vat: number;
        total: number;
        vatRate: number;
        discount: number;
        discountType: string;
      };
    }
  | { type: SubscribeAction.SET_SCHOOL_DETAILS; payload: ISchoolDetails }
  | { type: SubscribeAction.CLEAR_SCHOOL_DETAILS }
  | { type: SubscribeAction.SET_REGISTRATION_TOKEN; payload: string }
  | { type: SubscribeAction.SET_CANCEL_SUB_CONFIRM_MSG; payload: boolean }
  | { type: SubscribeAction.SET_MEMBERSHIP_OPTION_INFO; payload: any }
  | { type: SubscribeAction.QUOTES_ID; payload: number | null }
  | { type: ManageUsersAction.SET_UPDATE_USERS_LIST; payload: boolean }
  | { type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST; payload: boolean }
  | {
      type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_SUBJECT_ID;
      payload: number | null;
    }
  | {
      type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_PARENT_FOLDER;
      payload: {
        parentsFolderId: number | null;
        parentsFolderName: string | null;
      };
    }
  | { type: CookieBanner.SET_HIDE_COOKIE_BANNER; payload: boolean };

export const misc = (state = initialState, action: Action): State => {
  switch (action.type) {
    case GeneralAction.TOGGLE_MOBILE_MENU:
      return {
        ...state,
        mobileMenuOpen: !state.mobileMenuOpen,
      };
    case GeneralAction.ACCEPT_COOKIES:
      return {
        ...state,
        acceptCookies: true,
      };
    case GeneralAction.TOGGLE_ACCESSIBILITY_MODE:
      return {
        ...state,
        accessibilityMode: !state.accessibilityMode,
      };
    case GeneralAction.TOGGLE_TOAST:
      return {
        ...state,
        toastStatus: action.payload,
      };
    case GeneralAction.TOGGLE_ERROR_TOAST:
      return {
        ...state,
        errorToastStatus: action.payload,
      };
    case PricingAction.SELECT_PACKAGES:
      return {
        ...state,
        selectedPackages: action.payload,
      };
    case PricingAction.SELECT_PACKAGE_IDS:
      return {
        ...state,
        selectedPackageIds: action.payload,
      };
    case PricingAction.CLEAR_SELECTED_PACKAGE:
      return {
        ...state,
        selectedPackages: [],
      };
    case PricingAction.SET_VALID_PROMO_CODE:
      return {
        ...state,
        validPromoCode: action.payload,
      };
    case PricingAction.SET_PO_NUMBER:
      return {
        ...state,
        poNumber: action.payload,
      };
    case PricingAction.CLEAR_PO_NUMBER:
      return {
        ...state,
        poNumber: '',
      };
    case PricingAction.SET_FINANCE_EMAIL:
      return {
        ...state,
        FinanceEmail: action.payload,
      };
    case PricingAction.CLEAR_FINANCE_EMAIL:
      return {
        ...state,
        FinanceEmail: '',
      };    
    case PricingAction.SET_SECOND_USER_DETAILS:
      return {
        ...state,
        secondUserDetails: action.payload,
      };   
    case PricingAction.SET_EXISTING_SECOND_USER:
      return {
        ...state,
        secondExistingUserDetails: action.payload,
      };  
    case PricingAction.SET_ADD_OR_UPGRADE_USER_DETAILS:
      return {
        ...state,
        addOrUpgradeUserDetails: action.payload,
      };         
    case PricingAction.SET_SCHOOL_ACCOUNT_CONTEXT:
      return {
        ...state,
        schoolAccountContext: action.payload,
      };
    case PricingAction.SET_QUOTE_PROMO_CODE_SLOTS:
      return {
        ...state,
        quotePromoCodeSlots: action.payload,
      };
    case BlogAction.SET_BLOG_IDS:
      return {
        ...state,
        blogIds: action.payload,
      };
    case CaseStudyAction.SET_CASE_STUDIES_IDS:
      return {
        ...state,
        caseStudiesIds: action.payload,
      };
    case SubscribeAction.SET_PRICE:
      return {
        ...state,
        subscriptionPrice: action.payload,
      };
    case SubscribeAction.SET_SCHOOL_DETAILS:
      return {
        ...state,
        schoolDetails: action.payload,
      };
    case SubscribeAction.CLEAR_SCHOOL_DETAILS:
      return {
        ...state,
        schoolDetails: null,
      };
    case SubscribeAction.SET_REGISTRATION_TOKEN:
      return {
        ...state,
        registrationToken: action.payload,
      };
    case SubscribeAction.SET_CANCEL_SUB_CONFIRM_MSG:
      return {
        ...state,
        cancelSubConfirmMsg: action.payload,
      };      
    case SubscribeAction.SET_MEMBERSHIP_OPTION_INFO:
      return {
        ...state,
        membershipOptionInfo: action.payload,
      };
    case SubscribeAction.QUOTES_ID:
      return {
        ...state,
        quotesId: action.payload,
      };
    case ManageUsersAction.SET_UPDATE_USERS_LIST:
      return {
        ...state,
        updateUsersList: action.payload,
      };
    case MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST:
      return {
        ...state,
        myDocumentsList: action.payload,
      };
    case MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_SUBJECT_ID:
      return {
        ...state,
        myDocumentsListSubjectId: action.payload,
      };
    case MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_PARENT_FOLDER:
      return {
        ...state,
        myDocumentsListParentFolderId: action.payload.parentsFolderId,
        myDocumentsListParentFolderName: action.payload.parentsFolderName,
        myDocumentsList: true,
      };
    case CookieBanner.SET_HIDE_COOKIE_BANNER:
      return {
        ...state,
        hideCookieBanner: action.payload,
      };
    default:
      return state;
  }
};

// Action creatorse

export const clearPackageForm = (): Action => ({
  type: PricingAction.CLEAR_SELECTED_PACKAGE,
});

export const updateSelectedPackages = (payload: Package[]): Action => ({
  type: PricingAction.SELECT_PACKAGES,
  payload,
});

export const updateSelectedPackageIds = (payload: number[]): Action => ({
  type: PricingAction.SELECT_PACKAGE_IDS,
  payload,
});

export const setValidPromoCode = (payload: string[]): Action => ({
  type: PricingAction.SET_VALID_PROMO_CODE,
  payload,
});

export const setPoNumber = (payload: string): Action => ({
  type: PricingAction.SET_PO_NUMBER,
  payload,
});

export const clearPoNumber = (): Action => ({
  type: PricingAction.CLEAR_PO_NUMBER,
});

export const setFinanceEmail = (payload: string): Action => ({
  type: PricingAction.SET_FINANCE_EMAIL,
  payload,
});

export const clearFinanceEmail = (): Action => ({
  type: PricingAction.CLEAR_FINANCE_EMAIL,
});

export const setSecondUserDetails = (payload: any): Action => ({
  type: PricingAction.SET_SECOND_USER_DETAILS,
  payload,
});

export const setSecondExistingUser = (payload: any): Action => ({
  type: PricingAction.SET_EXISTING_SECOND_USER,
  payload,
});

export const setAddOrUpgradeUserDetails = (payload: any): Action => ({
  type: PricingAction.SET_ADD_OR_UPGRADE_USER_DETAILS,
  payload,
});

export const setSchoolAccountContext = (payload: AccountContext | null): Action => ({
  type: PricingAction.SET_SCHOOL_ACCOUNT_CONTEXT,
  payload,
});

export const setQuotePromoCodeSlots = (payload: PromoCodeSlots): Action => ({
  type: PricingAction.SET_QUOTE_PROMO_CODE_SLOTS,
  payload,
});

export const setUpdateUsersList = (payload: boolean): Action => ({
  type: ManageUsersAction.SET_UPDATE_USERS_LIST,
  payload,
});

export const setUpdateMyDocumentsList = (payload: boolean): Action => ({
  type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST,
  payload,
});

export const setMyDocumentsListSubjectId = (
  payload: number | null
): Action => ({
  type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_SUBJECT_ID,
  payload,
});

export const setMyDocumentsListParentFolder = (payload: {
  parentsFolderId: number | null;
  parentsFolderName: string | null;
}): Action => ({
  type: MyDocumentsAction.SET_UPDATE_DOCUMENTS_LIST_PARENT_FOLDER,
  payload,
});

export const setPrice = (payload: {
  price: number;
  vat: number;
  total: number;
  discount: number;
  discountType: string;
}): Action => ({
  type: SubscribeAction.SET_PRICE,
  payload,
});
export const setSchoolDetails = (payload: ISchoolDetails): Action => ({
  type: SubscribeAction.SET_SCHOOL_DETAILS,
  payload,
});

export const clearSchoolDetails = (): Action => ({
  type: SubscribeAction.CLEAR_SCHOOL_DETAILS,
});

export const setRegistrationToken = (payload: string): Action => ({
  type: SubscribeAction.SET_REGISTRATION_TOKEN,
  payload,
});

export const setCancelSubConfirmMsg = (payload: boolean): Action => ({
  type: SubscribeAction.SET_CANCEL_SUB_CONFIRM_MSG,
  payload,
});

export const setHideCookieBanner = (payload: boolean): Action => ({
  type: CookieBanner.SET_HIDE_COOKIE_BANNER,
  payload,
});

export const setMembershipOptInfo = (payload: any): Action => ({
  type: SubscribeAction.SET_MEMBERSHIP_OPTION_INFO,
  payload,
});

export const setQuotesId = (payload: number | null): Action => ({
  type: SubscribeAction.QUOTES_ID,
  payload,
});

export const toogleToast = (payload: {
  open: boolean;
  message: string;
}): Action => ({
  type: GeneralAction.TOGGLE_TOAST,
  payload,
});

export const toogleErrorToast = (payload: {
  open: boolean;
  message: string;
}): Action => ({
  type: GeneralAction.TOGGLE_ERROR_TOAST,
  payload,
});

// Selectors

export const selectMobileMenuOpen = (store: Store) => store.misc.mobileMenuOpen;
export const selectAccessibilityMode = (store: Store) =>
  store.misc.accessibilityMode;
export const selectSelectedPackages = (store: Store) =>
  store.misc.selectedPackages;
export const selectSelectedPackageIds = (store: Store) =>
  store.misc.selectedPackageIds;
export const selectValidPromoCode = (store: Store) => store.misc.validPromoCode;
export const selectSubscriptionPrice = (store: Store) =>
  store.misc.subscriptionPrice;
export const selectPoNumber = (store: Store) => store.misc.poNumber;
export const selectFinanceEmail = (store: Store) => store.misc.FinanceEmail;
export const selectSecondUserDetails = (store: Store) => store.misc.secondUserDetails;
export const selectSecondExistingUser = (store: Store) => store.misc.secondExistingUserDetails;
export const selectAddOrUpgradeUserDetails = (store: Store) => store.misc.addOrUpgradeUserDetails;
export const selectSchoolAccountContext = (store: Store) => store.misc.schoolAccountContext;
export const selectQuotePromoCodeSlots = (store: Store) => store.misc.quotePromoCodeSlots;
export const selectUpdateUsersList = (store: Store) =>
  store.misc.updateUsersList;
export const selectUpdateMyDocumentsList = (store: Store) =>
  store.misc.myDocumentsList;
export const selectUpdateMyDocumentsListSubjectId = (store: Store) =>
  store.misc.myDocumentsListSubjectId;
export const selectUpdateMyDocumentsListParentFolderId = (store: Store) =>
  store.misc.myDocumentsListParentFolderId;
export const selectUpdateMyDocumentsListParentFolder = (store: Store) => ({
  parentFoldersId: store.misc.myDocumentsListParentFolderId,
  parentFoldersName: store.misc.myDocumentsListParentFolderName,
});
export const selectPrice = (store: Store) => store.misc.subscriptionPrice;
export const selectShoolDetails = (store: Store) => store.misc.schoolDetails;
export const selectRegistrationToken = (store: Store) =>
  store.misc.registrationToken;
export const selectCancelSubConfirmMsg = (store: Store) => store.misc.cancelSubConfirmMsg;
export const selectToastStatus = (store: Store) => store.misc.toastStatus;
export const selectErrorToastStatus = (store: Store) => store.misc.errorToastStatus;
export const selectHideCookieBanner = (store: Store) =>
  store.misc.hideCookieBanner;
export const selectMembershipOptInfo = (store: Store) => store.misc.membershipOptionInfo;
export const selectQuotesId = (store: Store) => store.misc.quotesId;
